// src/components/Modals/DeadlineChangeDialog.tsx
//
// The dialog behind the calendar icon on a Sales Order part, and
// behind the bulk edit in the Production Deadline column header. Same dialog
// either way: it takes a new date and the reasons for moving it, and only
// the "Applies to" line and the button's wording depend on how many parts
// it is about to move.
//
// Structured on QcDelayReasonDialog (the QC delay capture), which solved the
// same shape - a reason select whose choice decides whether the comment is
// required.
//
// The reasons are one multi-select rather than a primary/contributing pair.
// A deadline usually moves for more than one cause at once (the material is
// late *and* the customer re-confirmed), and nothing downstream treats one of
// them as the headline, so making the user rank them would be inventing a
// distinction the ticket does not ask for.
//
// Every date in the dialog reads "23 Jun 2026", the date field included. The
// native date input showed whatever the browser locale does (06/23/2026 or
// 23/06/2026), and a day and a month that can be swapped are exactly the
// confusion a deadline dialog must not have.
//
// The reason list is never hardcoded here. It comes from the DELAY_REASONS
// table via useDelayReasons - the same list as the QC delay and the order
// completion delay, edited without a deploy.
import { FunctionComponent, useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemText,
  MenuItem,
  TextField,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
// For the picker only: weeks start on Monday. Month names are the same as
// the default locale's, so the dates written with plain dayjs still match.
import "dayjs/locale/en-gb";
import { useDelayReasons } from "hooks/useDelayReasons";

export type DeadlineChangeSubmission = {
  deadline: string;
  reasons: string[];
  comment?: string;
};

type PropsType = {
  open: boolean;
  /** How many productions this will move. Drives the wording only. */
  partCount: number;
  /** The orders the parts belong to, for the title. */
  orderIds: number[];
  /** The identifiers ("#1234") of the parts it will move. */
  partIdentifiers: string[];
  /**
   * True only when the caller knows the parts are every part of the order.
   * Otherwise a bulk change lists the parts it covers.
   */
  appliesToAllParts?: boolean;
  /**
   * The deadline the date field starts on. Left empty for a bulk change over
   * parts that do not share one - prefilling any single part's date there
   * would be a guess presented as a default.
   */
  currentDeadline?: string | null;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: (submission: DeadlineChangeSubmission) => void;
};

/**
 * The date input wants YYYY-MM-DD, and a deadline is a day.
 *
 * The leading 10 characters are taken as they are rather than parsed: an
 * ISO timestamp at UTC midnight, read back in a negative-offset timezone,
 * lands on the previous day, and the prefilled date would silently be one day
 * earlier than the deadline it claims to show.
 */
const toDateInput = (value?: string | null): string =>
  value ? value.slice(0, 10) : "";

const DATE_FORMAT = "D MMM YYYY";

/** "#0042", as the order header in the table writes it. */
const orderNumber = (id: number): string =>
  "#" + id.toString().padStart(4, "0");

/**
 * "23 Jun → 26 Jun · 3 days later". The year is left off unless the move
 * crosses one - the full dates are on the lines above.
 */
const describeChange = (from: string, to: string): string => {
  const fromDay = dayjs(from);
  const toDay = dayjs(to);
  const format = fromDay.year() === toDay.year() ? "D MMM" : DATE_FORMAT;
  const days = toDay.diff(fromDay, "day");
  const amount = `${Math.abs(days)} ${Math.abs(days) === 1 ? "day" : "days"}`;
  return `${fromDay.format(format)} → ${toDay.format(format)} · ${amount} ${
    days > 0 ? "later" : "earlier"
  }`;
};

const DeadlineChangeDialog: FunctionComponent<PropsType> = ({
  open,
  partCount,
  orderIds,
  partIdentifiers,
  appliesToAllParts,
  currentDeadline,
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const { selectableOptions, isLoading, isUnavailable, labelFor } =
    useDelayReasons();

  const [deadline, setDeadline] = useState("");
  const [reasons, setReasons] = useState<string[]>([]);
  const [comment, setComment] = useState("");

  // Reopening the dialog on another row must not show the previous row's
  // date, so the fields are seeded every time it opens rather than once.
  useEffect(() => {
    if (!open) {
      return;
    }
    setDeadline(toDateInput(currentDeadline));
    setReasons([]);
    setComment("");
  }, [open, currentDeadline]);

  const needsComment = selectableOptions.some(
    (option) => option.requiresComment && reasons.includes(option.code)
  );

  const previousDeadline = toDateInput(currentDeadline);
  const deadlineValid = !!deadline && dayjs(deadline).isValid();
  // Over parts with different deadlines there is no single "before", and any
  // date moves at least one of them.
  const deadlineChanged =
    deadlineValid && (!previousDeadline || deadline !== previousDeadline);
  const commentMissing = needsComment && !comment.trim();

  // The button stays disabled until there is something valid to save, so
  // there is no separate error pass after a click.
  const canSubmit =
    deadlineChanged && reasons.length > 0 && !commentMissing;

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = () => {
    if (!canSubmit) {
      return;
    }
    onConfirm({
      deadline,
      reasons,
      comment: comment.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Change production deadline
        {orderIds.length > 0 &&
          ` — ${orderIds.length === 1 ? "Order" : "Orders"} ${orderIds
            .map(orderNumber)
            .join(", ")}`}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {partCount > 1 && appliesToAllParts ? (
            <>
              Applies to <strong>all parts</strong>.
            </>
          ) : (
            <>
              Applies to:{" "}
              <strong>
                {partIdentifiers.length === 1 ? "Part" : "Parts"}{" "}
                {partIdentifiers.join(", ")}
              </strong>
            </>
          )}
        </DialogContentText>
        <DialogContentText sx={{ mb: 2 }}>
          Production deadline:{" "}
          <strong>
            {previousDeadline
              ? dayjs(previousDeadline).format(DATE_FORMAT)
              : partCount > 1
              ? "differs between the parts"
              : "not set"}
          </strong>
          .
        </DialogContentText>

        {isUnavailable && (
          <DialogContentText sx={{ mb: 2 }} color="error">
            The reasons for change could not be loaded, so the deadline cannot
            be moved. Reload the page, and tell an administrator if it keeps
            happening.
          </DialogContentText>
        )}

        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
          <DatePicker
            label="New production deadline"
            format={DATE_FORMAT}
            value={deadline ? dayjs(deadline) : null}
            onChange={(value) => {
              setDeadline(value && value.isValid() ? value.format("YYYY-MM-DD") : "");
            }}
            slotProps={{
              // Otherwise the field pads the day ("01 Nov 2026") and stops
              // matching the dates written out around it.
              field: { shouldRespectLeadingZeros: true },
              textField: {
                fullWidth: true,
                required: true,
                helperText:
                  previousDeadline && deadlineChanged
                    ? describeChange(previousDeadline, deadline)
                    : undefined,
              },
            }}
            sx={{ mb: 2 }}
          />
        </LocalizationProvider>

        <TextField
          select
          fullWidth
          required
          sx={{ mb: 2 }}
          label="Reason for change"
          value={reasons}
          onChange={(e) => {
            // With `multiple`, MUI hands back the whole selection as an array,
            // which the DOM event type does not describe.
            const codes = e.target.value as unknown as string[];
            setReasons(codes);
          }}
          SelectProps={{
            multiple: true,
            // The default joins the raw codes; these are the table's labels.
            renderValue: (value) => (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
                {(value as string[]).map((code) => (
                  <Chip key={code} label={labelFor(code)} size="small" />
                ))}
              </div>
            ),
          }}
        >
          {selectableOptions.map((option) => (
            <MenuItem key={option.code} value={option.code}>
              <Checkbox size="small" checked={reasons.includes(option.code)} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          multiline
          minRows={2}
          required={needsComment}
          label="Comment"
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
          }}
          helperText={commentMissing ? "Required for the selected reason" : undefined}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={isSubmitting || isLoading || isUnavailable || !canSubmit}
        >
          {isSubmitting ? (
            <CircularProgress size={18} />
          ) : partCount === 1 ? (
            "Update deadline"
          ) : (
            `Update ${partCount} parts`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeadlineChangeDialog;
